// @ts-nocheck
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  useTheme,
  Card,
  CardContent,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import ReactMuiTableListView from '../ReactMuiTableListViewV8';
import { useDocumentLinkedEntitiesQuery } from '../../../features/document/api/documentInventoryQueries';
import { constructBreadcrumbState } from '../../../utils/Utils';
import { ReactComponent as InfoIcon } from '../../../assets/Icons/infoIcon.svg';
import { EntityIcon } from '../ReactFlow/NewNodeTypes';

const CustomEntityIdCell = ({ row }) => {
  const entity = row?.original;
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
      }}
    >
      <EntityIcon
        entity_type={entity?.entity_type}
        size={16}
        wrapperSize={22}
      />
      <Typography variant="body2">{entity?.unique_id}</Typography>
    </Stack>
  );
};

CustomEntityIdCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      unique_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      entity_type: PropTypes.string,
      request_type: PropTypes.string,
    }),
  }).isRequired,
};

const CustomEntityNameCell = ({ row }) => {
  const entity = row?.original;
  return (
    <Typography variant="body2">
      {entity?.entity_name || entity?.unique_id}
    </Typography>
  );
};

CustomEntityNameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      unique_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      entity_name: PropTypes.string,
    }),
  }).isRequired,
};

const DocumentLinkedEntities = ({ documentId, onCloseModal }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const { data: linkedEntitiesResponse, isLoading: loading } =
    useDocumentLinkedEntitiesQuery({
      params: {
        document_id: documentId,
        pageIndex: pageIndex + 1,
        pageSize,
      },
      options: {
        enabled: Boolean(documentId),
      },
    });
  const entities = linkedEntitiesResponse?.entities || [];
  const count = linkedEntitiesResponse?.count || 0;

  const handleFetchData = useCallback((_pageIndex, _pageSize) => {
    if (_pageIndex === 0) {
      setSkipPageReset(false);
    } else {
      setSkipPageReset(true);
    }
    setPageIndex(_pageIndex);
    setPageSize(_pageSize);
  }, []);

  const redirectToModel = useCallback(
    (entity) => {
      const currentUniqueId = entity?.unique_id;
      const dynamicEntityPath = entity?.entity_type_name;
      const type = entity?.entity_type;

      const breadcrumbState = constructBreadcrumbState(
        entity,
        [],
        true,
        currentUniqueId,
        dynamicEntityPath,
        type
      );

      navigate(
        { pathname: `/${dynamicEntityPath}/${currentUniqueId}` },
        { state: breadcrumbState }
      );
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        heading: 'Entity ID',
        Header: 'Entity ID',
        accessor: 'unique_id',
        width: 200,
        Cell: CustomEntityIdCell,
      },
      {
        heading: 'Entity Names',
        Header: 'Entity Names',
        accessor: 'entity_name',
        width: 200,
        Cell: CustomEntityNameCell,
      },
    ],
    []
  );

  const handleGetCellProps = useCallback(
    (cell) => {
      return {
        onDoubleClick: () => {
          redirectToModel(cell?.row?.original);
          if (onCloseModal) {
            onCloseModal();
          }
        },
      };
    },
    [redirectToModel, onCloseModal]
  );

  const handleGetRowProps = useCallback(() => ({}), []);

  if (loading && (!entities || entities.length === 0)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!loading && (!entities || entities.length === 0)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 2,
          width: '100%',
        }}
      >
        <Typography
          variant="body1"
          color="textSecondary"
          align="center"
          sx={{
            fontWeight: '500',
          }}
        >
          No linked entities impacted.
        </Typography>
      </Box>
    );
  }

  return (
    <Card sx={{ mt: 3, width: '100%' }}>
      <CardContent>
        <Typography
          variant="body1"
          color={theme.palette.documentLinkedEntities.titleText}
          sx={{
            mb: 1,
          }}
        >
          Impact on Associated Entities
        </Typography>

        <Box
          sx={{
            backgroundColor:
              theme.palette.documentLinkedEntities.warningBackground,
            p: 1.5,
            mb: 2,
            mt: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <InfoIcon style={{ width: '18px', height: '18px' }} />
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 0.5,
                }}
              >
                Note:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'list-item',
                  ml: 2,
                  listStyleType: 'disc',
                }}
              >
                This update impacts <b>{count} linked entities</b>.
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'list-item',
                  ml: 2,
                  listStyleType: 'disc',
                }}
              >
                Review the list below to apply the new version globally or
                customize the state for each entity.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box component={Card} className="table-card-wrapper">
          <ReactMuiTableListView
            data={entities}
            columns={columns}
            enablePagination={true}
            enableControlledPagination={true}
            fetchData={handleFetchData}
            dataLength={count}
            pageCount={Math.ceil(count / pageSize)}
            isAllOptionDisabled={true}
            getRowProps={handleGetRowProps}
            getCellProps={handleGetCellProps}
            maxTableHeight={200}
            initialPageSize={5}
            rowsPerPageOptions={[5, 10, 15]}
            skipPageReset={skipPageReset}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

DocumentLinkedEntities.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCloseModal: PropTypes.func,
};

export default DocumentLinkedEntities;
